/**
 * screens/DndScreen.tsx — DND & Blocked (Controls, bidirectional).
 * All/Outgoing/Incoming tabs; suppression list (scope chip, direction, source,
 * date, lift); Block-a-number modal. Framing: protection, not failure.
 * Wired to /v1/dnd (+ /:id/lift).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { Card, Chip, Button, ScreenBody, Modal } from '../components/voiceUi';
import { listDnd, addDndBlock, liftDndBlock } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

const TABS: Array<[string, string | undefined]> = [['All', undefined], ['Outgoing', 'outgoing'], ['Incoming', 'incoming']];

export default function DndScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { role, orgId } = useConsole();
  const canWrite = role !== 'viewer';

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [scope, setScope] = useState('all_voice');
  const [direction, setDirection] = useState('both');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await listDnd(TABS[tab][1]);
    setRows(data);
    setLoading(false);
  }, [tab]);
  useEffect(() => { void load(); }, [load, orgId]);

  const add = async () => {
    setBusy(true);
    try {
      await addDndBlock({ phone, scope, direction, ...(name.trim() ? { name: name.trim() } : {}), ...(reason.trim() ? { reason: reason.trim() } : {}) } as any);
      showSuccess(null, 'Added to block list');
      setOpen(false); setName(''); setPhone(''); setReason(''); setScope('all_voice'); setDirection('both');
      void load();
    } catch (e) { handleApiError(e, 'Action failed'); } finally { setBusy(false); }
  };

  const lift = async (id: string) => {
    try { await liftDndBlock(id); showSuccess(null, 'Block lifted'); void load(); }
    catch (e) { handleApiError(e, 'Action failed'); }
  };

  return (
    <ScreenBody maxWidth={1020}>
      {/* intro + Block a number */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <Text style={{ flex: 1, minWidth: 280, maxWidth: 660, fontSize: 12.5, color: c.ink3, lineHeight: 19 }}>
          The suppression list — numbers SK Voice must never dial (outgoing) or accept (incoming). Stop-requests on calls and campaign consent-misses land here automatically; you can also add numbers by hand. The engine checks this before every dial.
        </Text>
        {canWrite && <Button kind="primary" label="+ Block a number" onPress={() => setOpen(true)} />}
      </View>

      {/* tabs */}
      <View style={{ flexDirection: 'row', gap: 4, padding: 4, backgroundColor: c.paper3 ?? c.paper2, borderRadius: 9, marginBottom: 16, alignSelf: 'flex-start' }}>
        {TABS.map(([label], i) => (
          <Pressable key={label} onPress={() => setTab(i)} style={{ height: 30, paddingHorizontal: 16, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: tab === i ? c.card : 'transparent' }}>
            <Text style={{ fontSize: 12.5, fontWeight: tab === i ? '600' : '500', color: tab === i ? c.ink : c.ink3 }}>{label}</Text>
          </Pressable>
        ))}
      </View>

      {!loading && rows.length > 0 && (
        <Text style={{ alignSelf: 'flex-end', fontSize: 12, color: c.ink4, marginBottom: 8 }}>{rows.length} active suppression{rows.length === 1 ? '' : 's'}</Text>
      )}
      {loading ? <ActivityIndicator color={c.ink3} /> : rows.length === 0 ? (
        <Card><Text style={{ color: c.ink3, padding: 8 }}>No suppressions for this direction.</Text></Card>
      ) : (
        <Card>
          {/* header */}
          <View style={{ flexDirection: 'row', alignItems: 'center', paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: c.line }}>
            <Text style={[hStyle(c), { flex: 1 }]}>Contact</Text>
            <Text style={[hStyle(c), { width: 130 }]}>Scope</Text>
            <Text style={[hStyle(c), { width: 100 }]}>Direction</Text>
            <Text style={[hStyle(c), { width: 220 }]}>Source</Text>
            <Text style={[hStyle(c), { width: 70 }]}>Added</Text>
            <View style={{ width: 56 }} />
          </View>
          {rows.map((r) => <DndRow key={r.id} r={r} canWrite={canWrite} onLift={() => lift(r.id)} />)}
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} width={520}>
        <Text style={{ fontSize: 16, fontWeight: '700', color: c.ink }}>Block a number</Text>
        <Text style={{ fontSize: 12.5, color: c.ink3, marginTop: 3, marginBottom: 14 }}>Adds a voice suppression — enforced before every dial.</Text>
        {/* Name + Phone side by side */}
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}><LabeledInput label="Name · optional" value={name} onChange={setName} placeholder="Contact name" /></View>
          <View style={{ flex: 1 }}><LabeledInput label="Phone" value={phone} onChange={setPhone} placeholder="+91 …" mono /></View>
        </View>
        <SegmentedRow label="Scope" value={scope} onChange={setScope} options={[['all_voice', 'All voice'], ['promotional', 'Promotional only']]} />
        <SegmentedRow label="Direction" value={direction} onChange={setDirection} options={[['both', 'Both'], ['outgoing', 'Outgoing'], ['incoming', 'Incoming']]} />
        <LabeledInput label="Reason · optional" value={reason} onChange={setReason} placeholder="Why this number is blocked" />
        <View style={{ marginTop: 16 }}><Button kind="primary" label={busy ? 'Adding…' : 'Add to block list'} onPress={add} disabled={!phone || busy} /></View>
        <Text style={{ fontSize: 11, color: c.ink4, marginTop: 12, textAlign: 'center', lineHeight: 16 }}>All voice = never call for any reason. Promotional only = marketing blocked, transactional still allowed.</Text>
      </Modal>
    </ScreenBody>
  );
}

// Column-header text style.
function hStyle(c: any) {
  return { fontSize: 10.5, fontWeight: '600' as const, letterSpacing: 0.6, textTransform: 'uppercase' as const, color: c.ink4 };
}

// Short human source label + detail from the suppression reason.
function sourceLabel(src?: string): string {
  if (!src) return 'manual';
  return src.replace(/_/g, ' ');
}

// Scope-specific colors (match the design): promotional = amber, all_voice = red.
const AMBER = '#B8791F'; const RED = '#C4483B';

// A colored OUTLINED scope chip — amber for promotional, red for all_voice.
function ScopeChip({ scope }: { scope: string }) {
  const col = scope === 'promotional' ? AMBER : RED;
  const label = scope === 'all_voice' ? 'all voice' : scope;
  return (
    <View style={{ alignSelf: 'flex-start', borderWidth: 1, borderColor: col, borderRadius: 6, paddingVertical: 3, paddingHorizontal: 9 }}>
      <Text style={{ fontSize: 11.5, fontFamily: 'monospace', color: col }}>{label}</Text>
    </View>
  );
}

// One suppression row: avatar + name/number, scope, direction, source, date, delete.
function DndRow({ r, canWrite, onLift }: { r: any; canWrite: boolean; onLift: () => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  // Name first; if no name, the number; only if neither, the id — never blank.
  const hasName = Boolean(r.contactName);
  const primary = r.contactName || r.phone || r.customerId || '—';
  const secondary = hasName ? (r.phone ?? '') : '';
  const initials = hasName
    ? String(r.contactName).split(/\s+/).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    : '—';
  const dirLabel = r.direction === 'both' ? 'Both' : r.direction === 'outgoing' ? 'Outgoing' : r.direction === 'incoming' ? 'Incoming' : r.direction;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 16, borderTopWidth: 1, borderTopColor: c.line }}>
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <View style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: c.paper3 ?? c.paper2, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 11, fontWeight: '600', color: c.ink3 }}>{initials}</Text>
        </View>
        <View style={{ minWidth: 0 }}>
          <Text style={{ fontSize: 13.5, fontWeight: hasName ? '600' : '500', color: c.ink, fontFamily: hasName ? theme.font.sans : theme.font.mono }} numberOfLines={1}>{primary}</Text>
          {secondary ? <Text style={{ fontSize: 12, color: c.ink3, fontFamily: theme.font.mono, marginTop: 2 }} numberOfLines={1}>{secondary}</Text> : null}
        </View>
      </View>
      <View style={{ width: 130 }}><ScopeChip scope={r.scope} /></View>
      <Text style={{ width: 100, fontSize: 13, color: c.ink2 }}>{dirLabel}</Text>
      <View style={{ width: 220 }}>
        <Text style={{ fontSize: 13, color: c.ink2 }} numberOfLines={1}>{sourceLabel(r.source)}</Text>
        {r.reason ? <Text style={{ fontSize: 11.5, color: c.ink4, marginTop: 2 }} numberOfLines={1}>{r.reason}</Text> : null}
      </View>
      <Text style={{ width: 70, fontSize: 12, color: c.ink3 }} numberOfLines={1}>{new Date(r.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</Text>
      <View style={{ width: 56, alignItems: 'flex-end' }}>
        {canWrite && (
          <Pressable onPress={onLift} hitSlop={6} style={{ paddingVertical: 4, paddingHorizontal: 10, borderRadius: 6, borderWidth: 1, borderColor: c.lineStrong }}>
            <Text style={{ fontSize: 12.5, color: c.ink2 }}>Lift</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

function LabeledInput({ label, value, onChange, mono, placeholder }: { label: string; value: string; onChange: (v: string) => void; mono?: boolean; placeholder?: string }) {
  const { theme } = useTheme(); const c = theme.colors; const { TextInput } = require('react-native');
  return (
    <View style={{ marginTop: 10 }}>
      <Text style={{ fontSize: 12, color: c.ink2, marginBottom: 5 }}>{label}</Text>
      <TextInput value={value} onChangeText={onChange} placeholder={placeholder} placeholderTextColor={c.ink4}
        style={{ height: 38, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, color: c.ink, backgroundColor: c.card, fontFamily: mono ? theme.font.mono : theme.font.sans, fontSize: 13 }} />
    </View>
  );
}

// Full-width segmented toggle (like the design's Scope/Direction pickers).
function SegmentedRow({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: Array<[string, string]> }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ marginTop: 12 }}>
      <Text style={{ fontSize: 12, color: c.ink2, marginBottom: 6 }}>{label}</Text>
      <View style={{ flexDirection: 'row', gap: 4, padding: 4, backgroundColor: c.paper3 ?? c.paper2, borderRadius: 9 }}>
        {options.map(([v, l]) => {
          const sel = value === v;
          return (
            <Pressable key={v} onPress={() => onChange(v)} style={{ flex: 1, height: 34, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: sel ? c.ink : 'transparent' }}>
              <Text style={{ fontSize: 13, fontWeight: sel ? '600' : '500', color: sel ? c.card : c.ink3 }}>{l}</Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

